import {ScrollArea} from 'src/design/atoms/ScrollArea';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import classes from './SidebarScrollArea.module.css';

export interface SidebarScrollAreaProps {
  children: React.ReactNode;
  /** Narrower track for the 64px rail. */
  scrollbarSize?: number;
}

/** Sidebar link list: always-visible light scrollbar plus a bottom fade while more rows remain. */
export const SidebarScrollArea: React.FC<SidebarScrollAreaProps> = ({
  children,
  scrollbarSize = 8,
}) => {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [hasMoreBelow, setHasMoreBelow] = useState(false);

  const sync = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    setHasMoreBelow(
      viewport.scrollTop + viewport.clientHeight < viewport.scrollHeight - 1
    );
  }, []);

  useEffect(() => {
    const frame = requestAnimationFrame(sync);
    const viewport = viewportRef.current;
    const observer = viewport ? new ResizeObserver(sync) : undefined;
    if (viewport && observer) {
      observer.observe(viewport);
      Array.from(viewport.children).forEach(child => observer.observe(child));
    }
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [sync]);

  return (
    <div className={classes.wrapper} data-more-below={hasMoreBelow || undefined}>
      <ScrollArea
        h="100%"
        type="always"
        scrollbars="y"
        scrollbarSize={scrollbarSize}
        offsetScrollbars="y"
        viewportRef={viewportRef}
        onScrollPositionChange={sync}
        classNames={{scrollbar: classes.scrollbar, thumb: classes.thumb}}>
        {children}
      </ScrollArea>
    </div>
  );
};
